import { useMemo } from "react";

import { useAccountContext } from "~/v2/providers/AccountProvider";
import type { Account } from "../rest/account";

type UseGetLiveAccount = {
  account?: Account;
  isLoading: boolean;
  refetch: () => void;
};

/**
 * Returns the currentAccount from AccountContext if product === "live",
 * otherwise returns the trading account.
 *
 * - If product === "live", returns the current live account.
 * - If product === "paper", returns the trading account.
 */
export const useGetCurrentAccountIfLiveElseTradingAccount =
  (): UseGetLiveAccount => {
    const {
      product,
      currentAccount,
      liveAccounts,
      refetchLiveAccounts,
      isLoading,
    } = useAccountContext();

    const handleRefetch = () => {
      refetchLiveAccounts();
    };

    const account = useMemo(() => {
      if (product === "live") {
        return currentAccount;
      }

      const tradingAccount = (liveAccounts ?? []).find(
        (account) =>
          account.account_type === "trading" ||
          account.account_type === "legal_entity" ||
          // If account type is not set, assume it's a trading account.
          // TODO (MLA): Temporary fix until be backfill the empty account types.
          !account.account_type,
      );

      return tradingAccount;
    }, [product, currentAccount, liveAccounts]);

    return {
      account,
      isLoading,
      refetch: handleRefetch,
    };
  };

export const useGetTradingAccount = (): UseGetLiveAccount => {
  const { liveAccounts, refetchLiveAccounts, isLoading } = useAccountContext();

  const handleRefetch = () => {
    refetchLiveAccounts();
  };

  const account = useMemo(() => {
    return (liveAccounts ?? []).find(
      (account) =>
        account.account_type === "trading" ||
        account.account_type === "legal_entity" ||
        // If account type is not set, assume it's a trading account.
        // TODO (MLA): Temporary fix until be backfill the empty account types.
        !account.account_type,
    );
  }, [liveAccounts]);

  return {
    account,
    isLoading,
    refetch: handleRefetch,
  };
};
